import type { JSX } from "preact";
import { useRef, useState } from "preact/hooks";
import {
  bulkDelete,
  bulkMarkSeen,
  bulkMove,
  clearSearch,
  clearSelection,
  loadMessages,
  loadMoreMessages,
  openMessage,
  selectAll,
  toggleFlagged,
  toggleSelect,
  toggleSeen,
} from "../actions";
import {
  currentMailboxObj,
  hasMoreMessages,
  isSearching,
  listError,
  listLoading,
  loadingMore,
  mailboxes,
  messagesTotal,
  onlyUnread,
  searchError,
  searchLoading,
  searchQuery,
  searchResults,
  selectedUid,
  selectedUids,
  visibleMessages,
} from "../state";
import type { Envelope } from "../../shared/types";
import { avatarColor, faviconUrl, formatListDate, initials, senderLabel } from "../util";
import { Icon } from "./icons";

export function MessageList(): JSX.Element {
  const listRef = useRef<HTMLDivElement | null>(null);
  const box = currentMailboxObj.value;
  const searching = isSearching.value;
  const items = searching ? (searchResults.value ?? []) : visibleMessages.value;
  const title = box?.specialUse ?? box?.name ?? "Inbox";
  const selCount = selectedUids.value.size;
  const allSelected = items.length > 0 && selCount === items.length;
  const someSelected = selCount > 0 && !allSelected;

  // Find archive and trash paths for bulk actions.
  const archiveMbox = mailboxes.value.find((m) => m.specialUse === "archive");
  const trashMbox = mailboxes.value.find((m) => m.specialUse === "trash");
  const archivePath = archiveMbox?.path ?? "Archive";
  const trashPath = trashMbox?.path ?? "Trash";

  return (
    <section class="flex h-full w-[400px] shrink-0 flex-col border-r border-subtle bg-ink-0">
      <div class="flex h-12 shrink-0 items-center gap-2 border-b border-subtle px-3">
        <input
          type="checkbox"
          class="h-4 w-4 rounded border-subtle bg-ink-2 accent-accent"
          title="Select all"
          checked={allSelected}
          ref={(el: HTMLInputElement | null) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={() => {
            if (allSelected || someSelected) {
              clearSelection();
            } else {
              selectAll();
            }
          }}
        />
        <button class="icon-btn" title="Refresh" onClick={() => void loadMessages()}>
          <Icon name="refresh" size={16} />
        </button>
        <div class="ml-auto flex items-center gap-1">
          {!searching && (
            <>
              <button
                onClick={() => {
                  onlyUnread.value = !onlyUnread.value;
                }}
                class={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  onlyUnread.value ? "bg-accent text-fg-on-accent" : "text-fg-dim hover:bg-ink-3"
                }`}
              >
                Unread
              </button>
              <button class="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-fg-dim transition-colors hover:bg-ink-3">
                <Icon name="filter" size={14} /> Filter
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bulk action bar — shown when at least one message is selected */}
      {selCount > 0 && (
        <div class="flex flex-wrap items-center gap-1 border-b border-subtle bg-accent/10 px-3 py-1.5">
          <span class="mr-1 text-xs font-semibold text-fg-dim">{selCount} selected</span>
          <button
            class="rounded px-2 py-0.5 text-xs text-fg-dim hover:bg-ink-3"
            onClick={() => void bulkMarkSeen(true)}
          >
            Mark read
          </button>
          <button
            class="rounded px-2 py-0.5 text-xs text-fg-dim hover:bg-ink-3"
            onClick={() => void bulkMarkSeen(false)}
          >
            Mark unread
          </button>
          <button
            class="rounded px-2 py-0.5 text-xs text-fg-dim hover:bg-ink-3"
            onClick={() => void bulkMove(archivePath)}
          >
            Archive
          </button>
          <button
            class="rounded px-2 py-0.5 text-xs text-fg-dim hover:bg-ink-3"
            onClick={() => void bulkMove(trashPath)}
          >
            Move to Trash
          </button>
          <button
            class="rounded px-2 py-0.5 text-xs text-danger hover:bg-danger/10"
            onClick={() => void bulkDelete()}
          >
            Delete
          </button>
          <button
            class="ml-auto rounded px-2 py-0.5 text-xs text-muted hover:bg-ink-3"
            onClick={() => clearSelection()}
          >
            Clear
          </button>
        </div>
      )}

      {/* Search results header */}
      {searching ? (
        <div class="flex items-center justify-between border-b border-subtle px-3 py-2 text-xs">
          <span class="text-fg-dim">
            {searchLoading.value
              ? "Searching…"
              : `Results for "${searchQuery.value}" (${items.length})`}
          </span>
          <button
            class="rounded px-2 py-0.5 text-xs font-medium text-accent hover:bg-accent/10"
            onClick={() => clearSearch()}
          >
            Clear
          </button>
        </div>
      ) : (
        <div class="flex items-center justify-between px-3 py-2 text-xs text-muted">
          <span class="font-semibold capitalize text-fg-dim">{title}</span>
          <span class="tabular-nums">{messagesTotal.value}</span>
        </div>
      )}

      <div
        ref={listRef}
        class="min-h-0 flex-1 overflow-y-auto"
        onScroll={(e) => {
          if (searching) return;
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
            void loadMoreMessages();
          }
        }}
      >
        {searching && searchLoading.value && <ListSkeleton />}
        {searching && searchError.value && (
          <div class="p-6 text-center text-sm text-danger">{searchError.value}</div>
        )}
        {searching && !searchLoading.value && !searchError.value && items.length === 0 && (
          <div class="p-10 text-center text-sm text-muted">No results found</div>
        )}
        {searching && !searchLoading.value && !searchError.value &&
          items.map((m) => <Row key={`${m.mailbox}:${m.uid}`} m={m} />)}

        {!searching && listLoading.value && <ListSkeleton />}
        {!searching && !listLoading.value && listError.value && (
          <div class="p-6 text-center text-sm text-danger">{listError.value}</div>
        )}
        {!searching && !listLoading.value && !listError.value && items.length === 0 && (
          <div class="p-10 text-center text-sm text-muted">No messages</div>
        )}
        {!searching && !listLoading.value && !listError.value &&
          items.map((m) => <Row key={`${m.mailbox}:${m.uid}`} m={m} />)}
        {!searching && !listLoading.value && hasMoreMessages.value && (
          <div class="flex items-center justify-center py-3 text-xs text-muted">
            {loadingMore.value ? "Loading more…" : ""}
          </div>
        )}
      </div>
    </section>
  );
}

function SenderAvatar({ address, label, size }: { address: string; label: string; size: number }): JSX.Element {
  const [imgOk, setImgOk] = useState(true);
  const url = faviconUrl(address);

  if (url && imgOk) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        class="mt-0.5 h-9 w-9 shrink-0 rounded-full object-contain bg-ink-3 p-1"
        onError={() => setImgOk(false)}
      />
    );
  }
  return (
    <span
      class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-fg-on-accent"
      style={`background:${avatarColor(label)}`}
    >
      {initials(label)}
    </span>
  );
}

function Row({ m }: { m: Envelope }): JSX.Element {
  const active = selectedUid.value === m.uid;
  const checked = selectedUids.value.has(m.uid);
  const label = senderLabel(m);
  return (
    <div
      data-uid={String(m.uid)}
      draggable={true}
      onDragStart={(e) => {
        e.dataTransfer!.effectAllowed = "move";
        e.dataTransfer!.setData("text/plain", JSON.stringify({ uid: m.uid, mailbox: m.mailbox }));
      }}
      class={`group flex w-full items-start gap-2 border-b border-subtle px-2 py-2 text-left transition-colors ${
        active ? "bg-accent/15" : "hover:bg-ink-2"
      }`}
    >
      {/* Checkbox */}
      <input
        type="checkbox"
        class="mt-1 h-4 w-4 shrink-0 rounded border-subtle bg-ink-2 accent-accent"
        aria-label={`Select message: ${m.subject || "(no subject)"}`}
        checked={checked}
        onClick={(e) => {
          e.stopPropagation();
          toggleSelect(m.uid);
        }}
        onChange={() => {/* handled by onClick */}}
      />

      {/* Main clickable row area */}
      <button
        onClick={() => void openMessage(m.uid)}
        class="flex min-w-0 flex-1 gap-3 text-left"
      >
        <SenderAvatar address={m.from[0]?.address ?? ""} label={label} size={36} />
        <span class="min-w-0 flex-1">
          <span class="flex items-center gap-2">
            <span class={`flex-1 truncate text-sm ${m.seen ? "text-fg-dim" : "font-semibold text-fg"}`}>
              {label}
            </span>
            <span class="shrink-0 text-[11px] text-muted">{formatListDate(m.date)}</span>
          </span>
          <span
            class={`mt-0.5 block truncate text-[13px] ${
              m.seen ? "text-muted" : "font-medium text-fg-dim"
            }`}
          >
            {m.subject || "(no subject)"}
          </span>
          {(m.flagged || m.hasAttachments) && (
            <span class="mt-1 flex items-center gap-2 text-muted">
              {m.flagged && <Icon name="starFilled" size={13} class="text-warn" />}
              {m.hasAttachments && <Icon name="paperclip" size={13} />}
            </span>
          )}
        </span>
      </button>

      {/* Per-row action buttons */}
      <div class="flex shrink-0 flex-col items-center gap-1 pt-0.5">
        <button
          class="icon-btn h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 hover:!opacity-100"
          aria-label={m.flagged ? "Unstar" : "Star"}
          title={m.flagged ? "Unstar" : "Star"}
          onClick={(e) => {
            e.stopPropagation();
            void toggleFlagged(m.uid);
          }}
        >
          <Icon
            name={m.flagged ? "starFilled" : "star"}
            size={14}
            class={m.flagged ? "text-warn" : "text-muted"}
          />
        </button>
        <button
          class="icon-btn h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 hover:!opacity-100"
          aria-label={m.seen ? "Mark unread" : "Mark read"}
          title={m.seen ? "Mark unread" : "Mark read"}
          onClick={(e) => {
            e.stopPropagation();
            void toggleSeen(m.uid);
          }}
        >
          <Icon name={m.seen ? "mailOpen" : "inbox"} size={14} class="text-muted" />
        </button>
      </div>
    </div>
  );
}

function ListSkeleton(): JSX.Element {
  return (
    <div class="animate-pulse">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} class="flex gap-3 border-b border-subtle px-3 py-3">
          <span class="h-9 w-9 shrink-0 rounded-full bg-ink-3" />
          <span class="flex-1 space-y-2 py-1">
            <span class="block h-3 w-1/3 rounded bg-ink-3" />
            <span class="block h-3 w-2/3 rounded bg-ink-2" />
          </span>
        </div>
      ))}
    </div>
  );
}
